import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isAdmin, isStaff } from "@/lib/roles";
import { SnowDayButton } from "@/components/SnowDayButton";
import { useSearchParams } from "react-router-dom";

const TYPES = ["Closure", "Picture Day", "Field Trip", "Event", "Holiday", "Conference"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const tone = {
  Closure: "bg-red-100 text-red-700 border-red-200",
  "Picture Day": "bg-amber-100 text-amber-800 border-amber-200",
  "Field Trip": "bg-blue-100 text-blue-700 border-blue-200",
  Event: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Holiday: "bg-purple-100 text-purple-700 border-purple-200",
  Conference: "bg-slate-100 text-slate-700 border-slate-200",
};
const dot = {
  Closure: "bg-red-500", "Picture Day": "bg-amber-500", "Field Trip": "bg-blue-500",
  Event: "bg-emerald-500", Holiday: "bg-purple-500", Conference: "bg-slate-500",
};

const blank = { title: "", type: "Event", description: "", date: "", end_date: "",
                all_day: true, start_time: "", end_time: "", room: "All rooms" };

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const label = (d) => d.toLocaleDateString("en-US", { month: "long", year: "numeric" });

export default function CalendarPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const canEdit = isStaff(user);
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank);
  const [rooms, setRooms] = useState(["All rooms"]);

  const load = useCallback(async () => {
    const [{ data }, { data: roomRows }] = await Promise.all([api.get("/events", { params: { month: monthKey(cursor) } }), api.get("/rooms")]);
    setEvents(data); setRooms(["All rooms", ...roomRows.map((r) => r.room)]);
  }, [cursor]);

  useEffect(() => { load(); }, [load]);

  const shift = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));

  const openNew = useCallback((day) => {
    setEditing(null);
    setF({ ...blank, date: day || `${monthKey(cursor)}-01` });
    setOpen(true);
  }, [cursor]);
  useEffect(() => { if (params.get("action") === "add" && canEdit) openNew(params.get("day") || ""); }, [params, canEdit, openNew]);
  const openEdit = (e) => {
    setEditing(e.id);
    setF({ ...blank, ...e });
    setOpen(true);
  };

  const save = async () => {
    if (!f.title.trim() || !f.date) {
      toast.error("Add a title and a date");
      return;
    }
    const payload = { ...f };
    delete payload.id;
    delete payload.created_at;
    delete payload.created_by;
    try {
      if (editing) await api.put(`/events/${editing}`, payload);
      else await api.post("/events", payload);
      toast.success(editing ? "Event updated" : "Event added and families notified");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    try { await api.delete(`/events/${id}`); toast.success("Event removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [...Array(first.getDay()).fill(null),
                 ...Array.from({ length: days }, (_, i) => i + 1)];
  const today = new Date().toISOString().slice(0, 10);
  const dayStr = (d) => `${monthKey(cursor)}-${String(d).padStart(2, "0")}`;
  const onDay = (d) => events.filter((e) => e.date === dayStr(d)
    || (e.end_date && e.date <= dayStr(d) && dayStr(d) <= e.end_date));

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Calendar</p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Closures & events</h1>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-3">
            {isAdmin(user) && <SnowDayButton onDone={load} />}
            <Button onClick={() => openNew()} className="rounded-full h-12 px-7 bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="add-event-btn"><Plus size={18} className="mr-2" /> Add event</Button>
          </div>
        )}
      </div>

      <div className="mt-8 flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => shift(-1)} className="rounded-full"
                data-testid="cal-prev-btn"><CaretLeft size={18} /></Button>
        <p className="font-display text-2xl font-semibold w-56 text-center" data-testid="cal-month-label">{label(cursor)}</p>
        <Button variant="outline" size="icon" onClick={() => shift(1)} className="rounded-full"
                data-testid="cal-next-btn"><CaretRight size={18} /></Button>
        <Button variant="ghost" onClick={() => setCursor(new Date())} className="rounded-full font-bold"
                data-testid="cal-today-btn">Today</Button>
      </div>

      <div className="mt-6 grid lg:grid-cols-[1.5fr,1fr] gap-6">
        <div className="bg-white rounded-3xl border border-border p-5" data-testid="calendar-grid">
          <div className="grid grid-cols-7 gap-1 text-center">
            {DOW.map((d) => (
              <p key={d} className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground py-2">{d}</p>
            ))}
            {cells.map((d, i) => (
              <div key={i}
                   onClick={() => canEdit && d && openNew(dayStr(d))}
                   className={`min-h-[4.5rem] rounded-xl p-2 text-left ${
                     d ? "border border-border hover:border-[#3B82F6]" : ""} ${
                     d && dayStr(d) === today ? "bg-blue-50 border-[#3B82F6]" : ""} ${
                     canEdit && d ? "cursor-pointer" : ""}`}
                   style={{ transition: "border-color .2s ease" }}
                   data-testid={d ? `cal-day-${dayStr(d)}` : undefined}>
                {d && <p className="text-sm font-bold">{d}</p>}
                <div className="mt-1 space-y-1">
                  {d && onDay(d).slice(0, 2).map((e) => (
                    <p key={e.id} className="flex items-center gap-1 text-[11px] font-semibold truncate">
                      <span className={`h-2 w-2 rounded-full shrink-0 ${dot[e.type] || "bg-slate-400"}`} />
                      <span className="truncate">{e.title}</span>
                    </p>
                  ))}
                  {d && onDay(d).length > 2 && (
                    <p className="text-[11px] text-muted-foreground font-bold">+{onDay(d).length - 2} more</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3" data-testid="events-list">
          <h2 className="font-display text-xl font-semibold">This month</h2>
          {events.length === 0 && <p className="text-muted-foreground">Nothing on the calendar this month.</p>}
          {events.map((e) => (
            <div key={e.id} className="bg-white rounded-2xl border border-border p-5" data-testid={`event-card-${e.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <p className="font-display text-lg font-semibold truncate">{e.title}</p>
                  <p className="text-sm text-muted-foreground mt-1 font-semibold">
                    {e.date}{e.end_date ? ` – ${e.end_date}` : ""}
                    {!e.all_day && e.start_time ? ` · ${e.start_time}${e.end_time ? `–${e.end_time}` : ""}` : ""}
                  </p>
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full border shrink-0 ${tone[e.type]}`}>
                  {e.type}
                </span>
              </div>
              {e.description && <p className="mt-3 text-sm text-muted-foreground">{e.description}</p>}
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6]">{e.room}</p>
              {canEdit && (
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(e)} className="rounded-full font-bold"
                          data-testid={`edit-event-${e.id}`}><PencilSimple size={15} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => del(e.id)}
                          className="rounded-full font-bold text-[#EF4444]" data-testid={`delete-event-${e.id}`}>
                    <Trash size={15} />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit event" : "Add event"}</DialogTitle>
            <DialogDescription className="sr-only">
              Add or edit a closure, picture day, field trip or other event on the family calendar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold">Title</Label>
              <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                     placeholder="Closed for Thanksgiving" className="mt-2 h-11 rounded-xl" data-testid="event-title" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold">Type</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="event-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-bold">Who it applies to</Label>
                <Select value={f.room} onValueChange={(v) => setF({ ...f, room: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="event-room"><SelectValue /></SelectTrigger>
                  <SelectContent>{rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-bold">Date</Label>
                <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="event-date" />
              </div>
              <div>
                <Label className="text-sm font-bold">End date (optional)</Label>
                <Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })}
                       className="mt-2 h-11 rounded-xl" data-testid="event-end-date" />
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm font-semibold">
              <input type="checkbox" checked={f.all_day} onChange={(e) => setF({ ...f, all_day: e.target.checked })}
                     data-testid="event-all-day" /> All day
            </label>
            {!f.all_day && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold">Start time</Label>
                  <Input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })}
                         className="mt-2 h-11 rounded-xl" data-testid="event-start-time" />
                </div>
                <div>
                  <Label className="text-sm font-bold">End time</Label>
                  <Input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })}
                         className="mt-2 h-11 rounded-xl" data-testid="event-end-time" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-sm font-bold">Details</Label>
              <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                        className="mt-2 rounded-xl min-h-24" data-testid="event-description" />
            </div>
            <Button onClick={save} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="event-save-btn">{editing ? "Save changes" : "Add to calendar"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
